import { useState, useEffect } from 'react'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { useServices } from '../ServiceContext'
import type { ListDraftsParams, WireArticleFull, WireArticleListPage, RoleAssignments, WireWorkflowStage, WireBlockReason, WireArticle, WireArticleNote, WireArticleNoteRequest } from '../types'
import type { DraftUploadMetadata, ArticleSearchFilters, NavMethod } from '../services/articleService'
import type { PagePool } from '../types'
import { publishingApiClient } from '../http/publishingApiClient'

// ─── Query keys ───────────────────────────────────────────────────────────────

export const articleKeys = {
  all: ['articles'] as const,
  search: (q: string, filters?: ArticleSearchFilters) => ['articles', 'search', q, filters] as const,
  adminList: (params?: ListDraftsParams) => ['articles', 'admin', 'list', params] as const,
  adminDetail: (id: string) => ['articles', 'admin', 'detail', id] as const,
  reviewDetail: (id: string) => ['articles', 'review', 'detail', id] as const,
  published: (id: string) => ['articles', 'published', id] as const,
  recommended: () => ['articles', 'recommended'] as const,
  byIds: (ids: string[]) => ['articles', 'by-ids', [...ids].sort().join(',')] as const,
  savedPreview: (limit: number) => ['articles', 'saved', 'preview', limit] as const,
  savedList: () => ['articles', 'saved', 'list'] as const,
  recentlyRead: (limit: number) => ['articles', 'recently-read', limit] as const,
}

// ─── Canvas / page-block hooks ────────────────────────────────────────────────

export function useArticleSearch(query: string, filters?: ArticleSearchFilters) {
  const { articleService } = useServices()
  const [debounced, setDebounced] = useState(query)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(t)
  }, [query])

  const hasFilters = Object.keys(filters ?? {}).length > 0

  return useQuery({
    queryKey: articleKeys.search(debounced, filters),
    queryFn: () => articleService.search(debounced, filters),
    enabled: debounced.length >= 2 || hasFilters,
    staleTime: 30_000,
  })
}

/**
 * Fetches pool articles for a page-level pool configuration.
 * Always called (no conditional hook rules violated); only fires the
 * network request when `pool` is defined and has at least one filter.
 * Returns an object with `articles` and `isPending` so callers can gate rendering.
 */
export function usePagePoolArticles(pool?: PagePool): { articles: WireArticle[]; isPending: boolean } {
  const filters: ArticleSearchFilters = {}
  if (pool?.topicSlug) filters.issuesAndTopics = [pool.topicSlug]
  if (pool?.regionSlug) filters.regionsAndCountries = [pool.regionSlug]
  const hasFilters = Object.keys(filters).length > 0
  const query = pool?.query ?? ''

  const { data, isPending } = useArticleSearch(query, hasFilters ? filters : undefined)

  return {
    articles: pool ? (data ?? []) : [],
    isPending: pool ? isPending : false,
  }
}

// ─── Admin hooks ──────────────────────────────────────────────────────────────

export function useAdminArticles(params?: ListDraftsParams) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.adminList(params),
    queryFn: () => articleService.listAdmin(params),
    staleTime: 30_000,
  })
}

export function useAdminArticle(id: string) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.adminDetail(id),
    queryFn: () => articleService.getAdmin(id),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

export function usePublishedArticle(id: string, navContext?: { navMethod?: NavMethod; pageId?: string }) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.published(id),
    queryFn: () => articleService.getPublished(id, navContext?.navMethod, navContext?.pageId),
    enabled: Boolean(id),
    staleTime: 60_000,
  })
}

export function useRecommendedArticles() {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.recommended(),
    queryFn: () => articleService.listRecommendedArticles(),
    staleTime: 60_000,
  })
}

export function useArticlesByIds(ids: string[]) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.byIds(ids),
    queryFn: () => articleService.getByIds(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
  })
}

// ─── Saved & recently read ────────────────────────────────────────────────────

/** Top N saved articles for the MyWire mini-table preview. */
export function useSavedArticlesPreview(limit = 3) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.savedPreview(limit),
    queryFn: () => articleService.listSavedArticles({ limit }),
    staleTime: 30_000,
  })
}

/** Cursor-paginated saved-articles list for the "See all" page — fetch a page at a time via `fetchNextPage`. */
export function useSavedArticlesList() {
  const { articleService } = useServices()
  return useInfiniteQuery({
    queryKey: articleKeys.savedList(),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => articleService.listSavedArticles({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
  })
}

/**
 * Recently-read articles, most-recently-read first. Always a single page
 * (the API caps the list at RECENTLY_READ_LIMIT and never returns a cursor) —
 * reused as-is for both the MyWire mini-preview (limit=3) and the "See all"
 * page (limit=30, paged client-side since there's nothing more to fetch).
 */
export function useRecentlyRead(limit = 30) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.recentlyRead(limit),
    queryFn: () => articleService.listRecentlyRead({ limit }),
    staleTime: 0,
  })
}

function removeArticleFromPage(page: WireArticleListPage, articleId: string): WireArticleListPage {
  return { ...page, items: page.items.filter((a) => a.id !== articleId) }
}

/**
 * Single toggle backing every save/unsave surface (MyWire rows, article toolbar).
 * Pass the target state explicitly via `nextSaved` — the mutation dispatches to
 * the matching idempotent endpoint and lets the response be the source of truth.
 *
 * Optimistic update: unsaving removes the article from cached saved-article lists
 * immediately (matches the "remove it from my list" mental model). Saving does NOT
 * optimistically insert into those lists — `onSettled` invalidation pulls the
 * canonical order/contents from the server instead.
 */
export function useToggleSavedArticle() {
  const { articleService } = useServices()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ article, nextSaved }: { article: WireArticle; nextSaved: boolean }) =>
      nextSaved ? articleService.saveUserArticle(article.id) : articleService.unsaveUserArticle(article.id),

    onMutate: async ({ article, nextSaved }) => {
      await queryClient.cancelQueries({ queryKey: ['articles', 'saved'] })

      const previousPreview = queryClient.getQueriesData<WireArticleListPage>({ queryKey: ['articles', 'saved', 'preview'] })
      const previousList = queryClient.getQueriesData<InfiniteData<WireArticleListPage>>({ queryKey: ['articles', 'saved', 'list'] })
      const previousRecentlyRead = queryClient.getQueriesData<WireArticleListPage>({ queryKey: ['articles', 'recently-read'] })
      const previousPublished = queryClient.getQueryData<WireArticleFull>(articleKeys.published(article.id))

      if (!nextSaved) {
        queryClient.setQueriesData<WireArticleListPage>(
          { queryKey: ['articles', 'saved', 'preview'] },
          (old) => old && removeArticleFromPage(old, article.id),
        )
        queryClient.setQueriesData<InfiniteData<WireArticleListPage>>(
          { queryKey: ['articles', 'saved', 'list'] },
          (old) => old && { ...old, pages: old.pages.map((p) => removeArticleFromPage(p, article.id)) },
        )
      }

      // Keep recently-read and published-article caches in sync so that
      // navigating between the list and the article view sees a consistent state.
      queryClient.setQueriesData<WireArticleListPage>(
        { queryKey: ['articles', 'recently-read'] },
        (old) => old ? {
          ...old,
          items: old.items.map((item) => item.id === article.id ? { ...item, saved: nextSaved } : item),
        } : old,
      )
      queryClient.setQueryData<WireArticleFull>(
        articleKeys.published(article.id),
        (old) => old ? { ...old, saved: nextSaved } : old,
      )

      return { previousPreview, previousList, previousRecentlyRead, previousPublished }
    },

    onError: (_err, { article }, context) => {
      context?.previousPreview.forEach(([key, data]) => queryClient.setQueryData(key, data))
      context?.previousList.forEach(([key, data]) => queryClient.setQueryData(key, data))
      context?.previousRecentlyRead?.forEach(([key, data]) => queryClient.setQueryData(key, data))
      if (context?.previousPublished !== undefined) {
        queryClient.setQueryData(articleKeys.published(article.id), context.previousPublished)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['articles', 'saved'] })
      queryClient.invalidateQueries({ queryKey: ['articles', 'recently-read'] })
    },
  })
}

export function useUploadDraft() {
  const { articleService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, metadata }: { file: File; metadata: DraftUploadMetadata }) =>
      articleService.uploadDraft(file, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.all })
    },
  })
}

export function useDeleteAdminArticle() {
  const { articleService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => articleService.deleteAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.all })
    },
  })
}

// ─── Review hooks ─────────────────────────────────────────────────────────────

/** Fetch a full article (with blocks) for the reviewer. */
export function useReviewArticle(id: string) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: articleKeys.reviewDetail(id),
    queryFn: () => articleService.getReview(id),
    enabled: Boolean(id),
    staleTime: 60_000,
  })
}

/** Persist a full article (metadata + blocks) from the reviewer. */
export function useSaveReviewArticle() {
  const { articleService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (article: WireArticleFull) => articleService.saveReview(article),
    onSuccess: (_data, article) => {
      queryClient.invalidateQueries({ queryKey: articleKeys.reviewDetail(article.id) })
    },
  })
}

/**
 * Assign (or unassign) roles on an article via GET + PUT.
 * Fetches the full article body first so the PUT doesn't lose any blocks,
 * then applies all per-role assignments and PUTs the whole thing back.
 */
export function useAssignArticle() {
  const { articleService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, assignments }: { id: string; assignments: RoleAssignments }) => {
      const full = await articleService.getReview(id)
      const workflow: typeof full.workflow = {
        ...full.workflow,
        qcAssignedTo:        assignments.qcAssignedTo        ?? undefined,
        layoutAssignedTo:    assignments.layoutAssignedTo    ?? undefined,
        editorialAssignedTo: assignments.editorialAssignedTo ?? undefined,
        postPubAssignedTo:   assignments.postPubAssignedTo   ?? undefined,
      }
      await articleService.saveReview({ ...full, workflow })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.all })
    },
  })
}

// ─── Workflow action hook ─────────────────────────────────────────────────────

/**
 * Low-level workflow mutations.
 * Stage transitions use PATCH /drafts/:id with a stage-only payload.
 * Non-stage workflow updates continue to use GET+PUT so the full article body
 * is never lost.
 *
 * handoff            — changes workflow.stage without recording a sign-off
 * qc_signoff         — records cycle.qcSignoff; auto-advances to editorial_approval
 *                      if layoutSignoff is already set
 * layout_signoff     — records cycle.layoutSignoff; auto-advances if qcSignoff set
 * editorial_approve  — records cycle.editorialApproval; advances to 'scheduled';
 *                      stores scheduledFor on workflow (pass now for immediate publish)
 * editorial_reject   — closes cycle with editorialRejection + note; opens a new
 *                      cycle and resets stage to 'qc_review'
 * reschedule         — updates workflow.scheduledFor on an already-scheduled article
 * unschedule         — like editorial_reject: closes cycle with note, opens a new
 *                      cycle and resets stage to 'qc_review'; clears scheduledFor
 * post_pub_approve   — records postPubOutcome 'fully_approved'; calls the promote
 *                      endpoint to commit the article to the Data Repository; stage → 'published'
 * post_pub_revision  — records postPubOutcome 'revision_requested'; closes cycle; opens
 *                      a new cycle; stage → 'qc_review'; article remains visible to readers
 * post_pub_recall    — records postPubOutcome 'recalled'; stage → 'recalled';
 *                      sets visibleToReaders = false
 */
export type WorkflowActionInput =
  | { type: 'handoff';           id: string; targetStage: WireWorkflowStage }
  | { type: 'qc_signoff';        id: string; by: string; byName?: string }
  | { type: 'layout_signoff';    id: string; by: string; byName?: string }
  /** scheduledFor is an ISO 8601 string. Pass new Date().toISOString() for "publish now". */
  | { type: 'editorial_approve'; id: string; by: string; byName?: string; scheduledFor: string }
  | { type: 'editorial_reject';  id: string; by: string; byName?: string; note: string }
  /** Update the publication time on an already-scheduled article. */
  | { type: 'reschedule';        id: string; scheduledFor: string }
  /** Return a scheduled article to QC, treating it like an editorial rejection. */
  | { type: 'unschedule';        id: string; by: string; byName?: string; note: string }
  /** Approve a post-pub review: records outcome and calls the promote endpoint. */
  | { type: 'post_pub_approve';  id: string; by: string; byName?: string }
  /** Request revision: closes cycle, opens new one, stage → 'qc_review'; article stays visible. */
  | { type: 'post_pub_revision'; id: string; by: string; byName?: string; note: string }
  /** Recall: closes cycle, stage → 'recalled', sets visibleToReaders = false, writes revisionType. */
  | { type: 'post_pub_recall';   id: string; by: string; byName?: string; note: string; recallType: 'substantive-recall' | 'administrative-recall' | 'compliance-recall' }

export function useWorkflowAction() {
  const { articleService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: WorkflowActionInput) => {
      if (input.type === 'handoff') {
        await articleService.patchWorkflowStage(input.id, input.targetStage)
        return
      }

      const full = await articleService.getReview(input.id)
      const cycleIdx = full.workflow.currentCycle - 1
      const cycles = [...(full.workflow.cycles ?? [])]
      let workflow = { ...full.workflow }
      let stagePatch: WireWorkflowStage | undefined

      if (input.type === 'reschedule') {
        workflow = { ...workflow, scheduledFor: input.scheduledFor }
      } else if (input.type === 'unschedule') {
        const now = new Date().toISOString()
        const cycle = { ...(cycles[cycleIdx] ?? { cycleNumber: workflow.currentCycle, startedAt: now }) }
        cycle.editorialRejection = { by: input.by, byName: input.byName, at: now, note: input.note }
        cycle.closedBy = { reason: 'editorial_rejection', note: input.note, by: input.by, at: now }
        cycles[cycleIdx] = cycle
        const newCycleNumber = workflow.currentCycle + 1
        cycles.push({ cycleNumber: newCycleNumber, startedAt: now })
        const { scheduledFor: _drop, ...workflowWithoutSchedule } = workflow
        workflow = { ...workflowWithoutSchedule, cycles, currentCycle: newCycleNumber, stage: 'qc_review' }
        await articleService.saveReview({ ...full, workflow })
        return
      } else if (input.type === 'post_pub_approve') {
        const now = new Date().toISOString()
        const cycle = { ...(cycles[cycleIdx] ?? { cycleNumber: workflow.currentCycle, startedAt: now }) }
        cycle.postPubOutcome = 'fully_approved'
        cycle.postPubSignoff = { by: input.by, byName: input.byName, at: now }
        cycles[cycleIdx] = cycle
        // Save cycle data with stage still at 'post_pub_review' — promote() owns the → 'published' transition.
        workflow = { ...workflow, cycles }
        await articleService.saveReview({ ...full, workflow })
        await articleService.promote(input.id)
        return
      } else if (input.type === 'post_pub_revision') {
        const now = new Date().toISOString()
        const cycle = { ...(cycles[cycleIdx] ?? { cycleNumber: workflow.currentCycle, startedAt: now }) }
        cycle.postPubOutcome = 'revision_requested'
        cycle.postPubSignoff = { by: input.by, byName: input.byName, at: now, note: input.note }
        cycle.closedBy = { reason: 'post_pub_revision', note: input.note, by: input.by, at: now }
        cycles[cycleIdx] = cycle
        const newCycleNumber = workflow.currentCycle + 1
        cycles.push({ cycleNumber: newCycleNumber, startedAt: now })
        // visibleToReaders intentionally NOT changed — article stays live during revision
        workflow = { ...workflow, cycles, currentCycle: newCycleNumber, stage: 'qc_review' }
        await articleService.saveReview({ ...full, workflow })
        return
      } else if (input.type === 'post_pub_recall') {
        const now = new Date().toISOString()
        const cycle = { ...(cycles[cycleIdx] ?? { cycleNumber: workflow.currentCycle, startedAt: now }) }
        cycle.postPubOutcome = 'recalled'
        cycle.postPubSignoff = { by: input.by, byName: input.byName, at: now, note: input.note }
        cycles[cycleIdx] = cycle
        workflow = { ...workflow, cycles, stage: 'recalled' }
        await articleService.saveReview({ ...full, revisionType: input.recallType, visibleToReaders: false, workflow })
        return
      } else {
        const now = new Date().toISOString()
        const cycle = { ...(cycles[cycleIdx] ?? { cycleNumber: workflow.currentCycle, startedAt: now }) }

        if (input.type === 'qc_signoff') {
          cycle.qcSignoff = { by: input.by, byName: input.byName, at: now }
        } else if (input.type === 'layout_signoff') {
          cycle.layoutSignoff = { by: input.by, byName: input.byName, at: now }
        } else if (input.type === 'editorial_approve') {
          cycle.editorialApproval = { by: input.by, byName: input.byName, at: now }
          cycles[cycleIdx] = cycle
          // PUT: write cycle data + scheduledFor WITHOUT changing stage so the
          // subsequent PATCH can record the editorial_approval → scheduled transition
          // in article_workflow_history (the metrics pipeline only captures PATCH transitions)
          workflow = { ...workflow, cycles, scheduledFor: input.scheduledFor }
          await articleService.saveReview({ ...full, workflow })
          // PATCH: fires the stage transition and writes to article_workflow_history
          await articleService.patchWorkflowStage(input.id, 'scheduled')
          return
        } else {
          // editorial_reject — closes the current cycle and opens a new one
          cycle.editorialRejection = { by: input.by, byName: input.byName, at: now, note: input.note }
          cycle.closedBy = { reason: 'editorial_rejection', note: input.note, by: input.by, at: now }
          cycles[cycleIdx] = cycle
          const newCycleNumber = workflow.currentCycle + 1
          cycles.push({ cycleNumber: newCycleNumber, startedAt: now })
          workflow = { ...workflow, cycles, currentCycle: newCycleNumber, stage: 'qc_review' }
          await articleService.saveReview({ ...full, workflow })
          return
        }

        cycles[cycleIdx] = cycle
        workflow = { ...workflow, cycles }

        // Both signed off → advance to editorial; otherwise cross-pass between QC and Layout
        if (cycle.qcSignoff && cycle.layoutSignoff) {
          stagePatch = 'editorial_approval'
        } else if (input.type === 'qc_signoff') {
          stagePatch = 'layout_review'
        } else if (input.type === 'layout_signoff') {
          stagePatch = 'qc_review'
        }
      }

      await articleService.saveReview({ ...full, workflow })
      if (stagePatch && stagePatch !== full.workflow.stage) {
        await articleService.patchWorkflowStage(input.id, stagePatch)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.all })
    },
  })
}

/** Fetch the audit changelog for a draft. Lazy — only fires when enabled=true. */
export function useArticleChangelog(id: string, enabled: boolean) {
  const { articleService } = useServices()
  return useQuery({
    queryKey: [...articleKeys.reviewDetail(id), 'changelog'],
    queryFn: () => articleService.getChangelog(id),
    enabled: Boolean(id) && enabled,
    staleTime: 30_000,
  })
}

// ─── Block / unblock hook ─────────────────────────────────────────────────────────────

export type BlockArticleInput =
  | { action: 'block';   id: string; reason: WireBlockReason; additionalInfo: string; by: string; byName?: string }
  | { action: 'unblock'; id: string; by: string; byName?: string }

function inferUnblockTargetStage(workflow: WireArticleFull['workflow']): WireWorkflowStage {
  const rawStage = workflow.stage as string
  if (rawStage !== 'blocked') return workflow.stage

  const cycle = workflow.cycles?.[workflow.currentCycle - 1]
  if (workflow.scheduledFor || cycle?.editorialApproval) return 'scheduled'
  if (cycle?.qcSignoff && cycle?.layoutSignoff) return 'editorial_approval'
  if (cycle?.qcSignoff) return 'layout_review'
  if (cycle?.layoutSignoff) return 'qc_review'
  return 'qc_review'
}

export function useBlockArticle() {
  const { articleService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: BlockArticleInput) => {
      if (input.action === 'block') {
        await articleService.patchWorkflowStage(input.id, 'blocked', {
          reason: input.reason,
          notes: input.additionalInfo,
        })
      } else {
        const full = await articleService.getReview(input.id)
        await articleService.patchWorkflowStage(input.id, inferUnblockTargetStage(full.workflow))
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.all })
    },
  })
}

// ─── Notes hooks ──────────────────────────────────────────────────────────────

const noteKeys = {
  list: (draftId: string) => ['notes', draftId] as const,
}

/** Fetch all notes for a draft (public + caller's own private). Lazy — only fires when enabled=true. */
export function useArticleNotes(draftId: string, enabled: boolean) {
  return useQuery({
    queryKey: noteKeys.list(draftId),
    queryFn: async () => {
      const { data } = await publishingApiClient.get<WireArticleNote[]>(`/drafts/${draftId}/notes`)
      return data
    },
    enabled: Boolean(draftId) && enabled,
    staleTime: 30_000,
  })
}

/** Create an article-level or block-level note. */
export function useCreateNote(draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ blockId, content, isPrivate }: WireArticleNoteRequest & { blockId?: string }) => {
      const url = blockId
        ? `/drafts/${draftId}/notes/${blockId}`
        : `/drafts/${draftId}/notes`
      const { data } = await publishingApiClient.post<WireArticleNote>(url, { content, isPrivate })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.list(draftId) })
    },
  })
}

/** Delete a note by its note ID. */
export function useDeleteNote(draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (noteId: string) => {
      await publishingApiClient.delete(`/drafts/notes/${noteId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.list(draftId) })
    },
  })
}
